import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton 
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const NavBar = () => {
  const { isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth0();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    handleClose();
  };
  
  // Check if the user is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated || !user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Include the email as a query parameter
        const email = user.email;
        // Fix URL construction to prevent double /api
        const url = apiUrl === '/api' 
          ? `/api/auth/check?email=${encodeURIComponent(email)}` 
          : `${apiUrl}/auth/check?email=${encodeURIComponent(email)}`;
        console.log('NavBar fetching auth check from:', url);
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('NavBar auth check response status:', response.status);
        
        // Only try to parse JSON if the response is successful
        if (response.ok) {
          const data = await response.json();
          console.log('NavBar auth check data:', data);
          setIsAdmin(data.isAdmin);
        } else {
          console.log('NavBar auth check failed with status:', response.status);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Purkupojat Palautussovellus
        </Typography>
        
        {isAuthenticated ? (
          <>
            <Button color="inherit" component={RouterLink} to="/">Palautukset</Button>
            {isAdmin && (
              <Button color="inherit" component={RouterLink} to="/admin">Hallinta</Button>
            )}
            
            <Box sx={{ ml: 2 }}>
              <IconButton
                onClick={handleMenu}
                color="inherit"
                size="small"
                sx={{ ml: 2 }}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                {user?.picture ? (
                  <Avatar src={user.picture} alt={user.name} sx={{ width: 32, height: 32 }} />
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>
              <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'account-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user?.name}</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>Kirjaudu ulos</MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          <Button 
            color="inherit" 
            onClick={() => loginWithRedirect()}
          >
            Kirjaudu sisään
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
